"use client";

import { useState } from 'react';
import { useCookieConsent } from './CookieContext';

type ConsentLevel = 'all' | 'essential' | null;

export default function CookieBanner() {
  // Sdílený stav z CookieContext - díky tomu je lišta vždy synchronní se zbytkem webu
  // (např. když návštěvník povolí cookies tlačítkem v sekci s recenzemi).
  // Ukládání do localStorage řeší CookieProvider.
  const { consent, setConsent } = useCookieConsent();
  const [showSettings, setShowSettings] = useState(false);

  const handleAcceptAll = () => {
    setConsent('all');
  };

  const handleEssentialOnly = () => {
    setConsent('essential');
  };

  const handleSaveSettings = (newConsent: ConsentLevel) => {
    setConsent(newConsent || 'essential');
    setShowSettings(false);
  };

  // Don't show banner if user has already made a choice
  if (consent) {
    return null;
  }

  return (
    <div style={{
      position: 'fixed',
      bottom: 0,
      left: 0,
      right: 0,
      background: '#1F2E46',
      borderTop: '2px solid #FDC30F',
      padding: '1.5rem',
      zIndex: 9999,
      boxShadow: '0 -4px 20px rgba(0,0,0,0.3)'
    }}>
      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 1rem' }}>
        {!showSettings ? (
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '1rem',
            alignItems: 'center',
            textAlign: 'center'
          }}>
            <p style={{
              color: '#fff',
              fontSize: '1rem',
              lineHeight: 1.5,
              margin: 0,
              maxWidth: '800px'
            }}>
              Používáme soubory cookies pro zlepšení vašeho zážitku na našem webu. 
              Kliknutím na "Přijmout vše" souhlasíte s používáním všech cookies.
            </p>
            <div style={{
              display: 'flex',
              gap: '0.75rem',
              flexWrap: 'wrap',
              justifyContent: 'center'
            }}>
              <button
                onClick={handleAcceptAll}
                style={{
                  background: '#FDC30F',
                  color: '#1F2E46',
                  border: 'none',
                  padding: '0.75rem 1.5rem',
                  borderRadius: '8px',
                  fontWeight: 700,
                  fontSize: '1rem',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.2)'
                }}
                onMouseOver={(e) => e.currentTarget.style.transform = 'scale(1.05)'}
                onMouseOut={(e) => e.currentTarget.style.transform = 'scale(1)'}
              >
                Přijmout vše
              </button>
              <button
                onClick={handleEssentialOnly}
                style={{
                  background: 'transparent',
                  color: '#FDC30F',
                  border: '2px solid #FDC30F',
                  padding: '0.75rem 1.5rem',
                  borderRadius: '8px',
                  fontWeight: 700,
                  fontSize: '1rem',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease'
                }}
                onMouseOver={(e) => e.currentTarget.style.background = 'rgba(253, 195, 15, 0.1)'}
                onMouseOut={(e) => e.currentTarget.style.background = 'transparent'}
              >
                Pouze nezbytné
              </button>
              <button
                onClick={() => setShowSettings(true)}
                style={{
                  background: 'transparent',
                  color: '#fff',
                  border: 'none',
                  padding: '0.75rem 1.5rem',
                  borderRadius: '8px',
                  fontWeight: 600,
                  fontSize: '1rem',
                  cursor: 'pointer',
                  textDecoration: 'underline',
                  transition: 'all 0.3s ease'
                }}
                onMouseOver={(e) => e.currentTarget.style.color = '#FDC30F'}
                onMouseOut={(e) => e.currentTarget.style.color = '#fff'}
              >
                Nastavení
              </button>
            </div>
          </div>
        ) : (
          <CookieSettings onSave={handleSaveSettings} onCancel={() => setShowSettings(false)} />
        )}
      </div>
    </div>
  );
}

function CookieSettings({ 
  onSave, 
  onCancel 
}: { 
  onSave: (consent: ConsentLevel) => void;
  onCancel: () => void;
}) {
  const [analytics, setAnalytics] = useState(false);
  const [marketing, setMarketing] = useState(false);

  return (
    <div style={{
      background: 'rgba(255,255,255,0.05)',
      borderRadius: '12px',
      padding: '1.5rem',
      maxWidth: '600px',
      margin: '0 auto'
    }}>
      <h3 style={{
        color: '#FDC30F',
        fontSize: '1.25rem',
        fontWeight: 700,
        marginTop: 0,
        marginBottom: '1rem'
      }}>
        Nastavení cookies
      </h3>
      
      <div style={{ marginBottom: '1.5rem' }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: '1rem',
          paddingBottom: '1rem',
          borderBottom: '1px solid rgba(253, 195, 15, 0.2)'
        }}>
          <div>
            <div style={{ color: '#fff', fontWeight: 600, marginBottom: '0.25rem' }}>
              Nezbytné cookies
            </div>
            <div style={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.875rem' }}>
              Nutné pro fungování webu
            </div>
          </div>
          <input
            type="checkbox"
            checked={true}
            disabled
            style={{
              width: '20px',
              height: '20px',
              cursor: 'not-allowed'
            }}
          />
        </div>

        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: '1rem',
          paddingBottom: '1rem',
          borderBottom: '1px solid rgba(253, 195, 15, 0.2)'
        }}>
          <div>
            <div style={{ color: '#fff', fontWeight: 600, marginBottom: '0.25rem' }}>
              Analytické cookies
            </div>
            <div style={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.875rem' }}>
              Pomáhají nám zlepšovat web
            </div>
          </div>
          <input
            type="checkbox"
            checked={analytics}
            onChange={(e) => setAnalytics(e.target.checked)}
            style={{
              width: '20px',
              height: '20px',
              cursor: 'pointer',
              accentColor: '#FDC30F'
            }}
          />
        </div>

        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <div>
            <div style={{ color: '#fff', fontWeight: 600, marginBottom: '0.25rem' }}>
              Marketingové cookies
            </div>
            <div style={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.875rem' }}>
              Používány pro personalizaci reklam
            </div>
          </div>
          <input
            type="checkbox"
            checked={marketing}
            onChange={(e) => setMarketing(e.target.checked)}
            style={{
              width: '20px',
              height: '20px',
              cursor: 'pointer',
              accentColor: '#FDC30F'
            }}
          />
        </div>
      </div>

      <div style={{
        display: 'flex',
        gap: '0.75rem',
        justifyContent: 'flex-end',
        flexWrap: 'wrap'
      }}>
        <button
          onClick={onCancel}
          style={{
            background: 'transparent',
            color: '#fff',
            border: 'none',
            padding: '0.75rem 1.5rem',
            borderRadius: '8px',
            fontWeight: 600,
            fontSize: '1rem',
            cursor: 'pointer',
            transition: 'all 0.3s ease'
          }}
          onMouseOver={(e) => e.currentTarget.style.color = '#FDC30F'}
          onMouseOut={(e) => e.currentTarget.style.color = '#fff'}
        >
          Zrušit
        </button>
        <button
          onClick={() => onSave(analytics || marketing ? 'all' : 'essential')}
          style={{
            background: '#FDC30F',
            color: '#1F2E46',
            border: 'none',
            padding: '0.75rem 1.5rem',
            borderRadius: '8px',
            fontWeight: 700,
            fontSize: '1rem',
            cursor: 'pointer',
            transition: 'all 0.3s ease',
            boxShadow: '0 2px 8px rgba(0,0,0,0.2)'
          }}
          onMouseOver={(e) => e.currentTarget.style.transform = 'scale(1.05)'}
          onMouseOut={(e) => e.currentTarget.style.transform = 'scale(1)'}
        >
          Uložit nastavení
        </button>
      </div>
    </div>
  );
}
